/**
 * GENEL SİTE TARAMASI SİHİRBAZI (Rank Math kurulum sihirbazı tarzı, 5 adım):
 *  1) Site & Tarama Kapsamı  → domain oluşturulur, GENEL tarama arka planda başlar
 *  2) Google Bağlantıları    → hesapta başka sitede bağlantı varsa TEK TIK "bu siteye de kullan"
 *                              (OAuth tekrarı yok); yoksa OAuth'a gider ve SİHİRBAZA GERİ DÖNER
 *  3) Sıralama Takibi        → önemli kelimeler eklenir (ücretsiz; her gece otomatik ölçülür)
 *  4) AI Takibi              → yapay zekâya sorulan sorular otomatik önerilir + eklenir (ücretsiz)
 *  5) Bitti                  → özet + yönlendirme
 * Her adım atlanabilir; OAuth dönüşü ?step=3&domainId=N ile kaldığı yerden sürer.
 */
import { useCreateDomain } from "@workspace/api-client-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Globe, ArrowLeft, Loader2, FileText, BarChart3, ArrowRight, CheckCircle2, AlertTriangle, Target, Sparkles, PartyPopper, Radar, Plus } from "lucide-react";
import { useState } from "react";
import { startGoogleConnect } from "@/lib/google-connect";

const formSchema = z.object({
  url: z.string().url({ message: "Geçerli bir URL giriniz (örn: https://example.com)" }),
  crawlSpeed: z.number().min(1).max(10),
  userAgent: z.string().min(1, { message: "User Agent boş bırakılamaz" }),
  respectRobotsTxt: z.boolean(),
  onlyContextualLinks: z.boolean(),
  maxPages: z.string().optional(),
  linkSuggestionScope: z.enum(["primary", "all"]),
});

const STEPS = ["Site & Kapsam", "Google", "Sıralama Takibi", "AI Takibi", "Bitti"] as const;
const MARKETS = [["tr", "Türkiye"], ["en", "ABD / İngilizce"], ["de", "Almanya"], ["fr", "Fransa"], ["ar", "Arapça"]] as const;

export default function NewDomain() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const createDomain = useCreateDomain();

  // OAuth dönüşü: /domains/new?step=3&domainId=N → sihirbaz kaldığı adımdan sürer
  const params = new URLSearchParams(window.location.search);
  const resumeStep = Number(params.get("step"));
  const resumeId = Number(params.get("domainId"));
  const [step, setStep] = useState<number>(resumeStep >= 2 && resumeStep <= 5 && Number.isFinite(resumeId) ? resumeStep : 1);
  const [createdId, setCreatedId] = useState<number | null>(Number.isFinite(resumeId) && resumeId > 0 ? resumeId : null);
  const cameFromOauth = resumeStep >= 2 && Number.isFinite(resumeId);

  // Adım sonuç özetleri (Bitti ekranında gösterilir)
  const [googleState, setGoogleState] = useState<"bagli" | "atlandi" | null>(cameFromOauth && resumeStep === 3 ? "bagli" : null);
  const [kwCount, setKwCount] = useState(0);
  const [promptCount, setPromptCount] = useState(0);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: "", crawlSpeed: 3, userAgent: "Geolopbot/1.0",
      respectRobotsTxt: true, onlyContextualLinks: true, maxPages: "200", linkSuggestionScope: "primary",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      const payload = {
        url: values.url,
        crawlConfig: {
          crawlSpeed: values.crawlSpeed,
          userAgent: values.userAgent,
          respectRobotsTxt: values.respectRobotsTxt,
          onlyContextualLinks: values.onlyContextualLinks,
          maxPages: values.maxPages ? parseInt(values.maxPages) : null,
          linkSuggestionScope: values.linkSuggestionScope,
        },
      };
      const created = await createDomain.mutateAsync({ data: payload });
      setCreatedId((created as { id?: number })?.id ?? null);
      toast({ title: "Genel site taraması başladı", description: "Tarama arka planda sürüyor — kalan adımları tamamlayalım." });
      setStep(2);
    } catch {
      toast({ title: "Hata", description: "Domain eklenirken bir sorun oluştu.", variant: "destructive" });
    }
  }

  /* ── ADIM 2: Google — hesap düzeyi mevcut bağlantı kontrolü ── */
  const { data: gAccount } = useQuery({
    queryKey: ["me-google-account"],
    enabled: step === 2,
    queryFn: async (): Promise<{ connected: boolean; email?: string; fromDomain?: string }> => {
      const r = await fetch("/api/me/google-account");
      return r.ok ? r.json() : { connected: false };
    },
  });
  const [reusing, setReusing] = useState(false);
  const reuseGoogle = async () => {
    if (!createdId || reusing) return;
    setReusing(true);
    try {
      const r = await fetch(`/api/domains/${createdId}/google/reuse`, { method: "POST" });
      const j = (await r.json().catch(() => ({}))) as { ok?: boolean; matched?: boolean; error?: string };
      if (!r.ok) throw new Error(j.error ?? "Kopyalanamadı");
      setGoogleState("bagli");
      toast({ title: "Google bağlantısı bu siteye de tanımlandı", description: j.matched ? "Search Console ve Analytics eşleşmesi otomatik yapıldı." : "Property eşleşmesi bulunamadı — Trafik Analizi'nden seçebilirsin." });
      setStep(3);
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setReusing(false); }
  };

  /* ── ADIM 3: Sıralama takibi ── */
  const [kwText, setKwText] = useState("");
  const [kwMarket, setKwMarket] = useState("tr");
  const [kwBusy, setKwBusy] = useState(false);
  const addKeywords = async () => {
    if (!createdId || !kwText.trim() || kwBusy) return;
    setKwBusy(true);
    try {
      const r = await fetch(`/api/domains/${createdId}/rank-tracking/keywords`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ keywords: kwText, market: kwMarket }),
      });
      const j = (await r.json().catch(() => ({}))) as { added?: number; error?: string };
      if (!r.ok) throw new Error(j.error ?? "Eklenemedi");
      setKwCount(j.added ?? 0);
      toast({ title: `${j.added ?? 0} kelime takibe alındı`, description: "Hacim ve zorluk çekildi; her gece otomatik ölçülecek." });
      setStep(4);
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setKwBusy(false); }
  };

  /* ── ADIM 4: AI takibi — otomatik öneri + ekleme ── */
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  // Manuel soru girişi — otomatik öneriye mahkûm olma (kullanıcı isteği)
  const [aiManuel, setAiManuel] = useState("");
  const ekleAiManuel = () => {
    const soru = aiManuel.trim();
    if (!soru) return;
    if (!aiSuggestions.some((s) => s.toLowerCase() === soru.toLowerCase())) setAiSuggestions([...aiSuggestions, soru]);
    setAiManuel("");
  };
  const suggestAi = async () => {
    if (!createdId || aiBusy) return;
    setAiBusy(true);
    try {
      const r = await fetch(`/api/domains/${createdId}/rank-tracking/suggest-prompts`, { method: "POST" });
      const j = (await r.json().catch(() => ({}))) as { suggestions?: string[] };
      const sugs = (j.suggestions ?? []).slice(0, 10);
      if (!sugs.length) { toast({ title: "Öneri üretilemedi", description: "Önce birkaç takip kelimesi ekle ya da taramanın bitmesini bekle — sonra Sıralama Takibi'nden ekleyebilirsin." }); return; }
      setAiSuggestions(sugs);
    } finally { setAiBusy(false); }
  };
  const addAiPrompts = async () => {
    if (!createdId || !aiSuggestions.length || aiBusy) return;
    setAiBusy(true);
    try {
      const r = await fetch(`/api/domains/${createdId}/rank-tracking/prompts`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ prompts: aiSuggestions.join("\n") }),
      });
      const j = (await r.json().catch(() => ({}))) as { added?: number };
      setPromptCount(j.added ?? 0);
      toast({ title: `${j.added ?? 0} AI sorusu takibe alındı`, description: "Her gece 4 AI motoruna sorulup anılman ölçülecek." });
      setStep(5);
    } finally { setAiBusy(false); }
  };

  const wizardUrl = (s: number) => `/domains/new?step=${s}&domainId=${createdId ?? ""}`;
  const stepBox = "rounded-xl border p-4";

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => setLocation("/")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">Genel Site Taraması</h1>
          <p className="text-muted-foreground mt-1">Sitenin tamamı taranır: sayfa envanteri, içerikler, iç link ağı ve konu kümeleri — tüm ajanların ortak temeli.</p>
        </div>
      </div>

      {/* Adım göstergesi (Rank Math tarzı) */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {STEPS.map((label, i) => {
          const n = i + 1;
          return (
            <div key={label} className="flex items-center gap-1.5">
              {i > 0 && <span className="w-5 h-px bg-border" />}
              <span className="inline-flex items-center gap-1.5 text-[12px]">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold"
                  style={step >= n ? { background: "#e83e3c", color: "#fff" } : { background: "var(--muted)", color: "var(--muted-foreground)" }}>
                  {step > n ? <CheckCircle2 className="w-4 h-4" /> : n}
                </span>
                <span className={step === n ? "font-semibold" : "text-muted-foreground"}>{label}</span>
              </span>
            </div>
          );
        })}
      </div>

      {/* ═══ ADIM 1: SİTE & KAPSAM ═══ */}
      {step === 1 && (
        <Card>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader>
                <CardTitle>Site & Tarama Kapsamı</CardTitle>
                <CardDescription>
                  Bu GENEL taramadır: sayfalar, içerikler, iç link ağı ve cluster yapısı çıkarılır.
                  Teknik denetim, backlink ve AI görünürlük gibi ajanlar bu temelin üstünde — performans ve
                  maliyet için — ihtiyaç duyduğunda ayrıca çalıştırılır (Planlama'dan takvime de koyabilirsin).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <FormField control={form.control} name="url" render={({ field }) => (
                  <FormItem>
                    <FormLabel>URL</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="https://example.com" className="pl-10" {...field} />
                      </div>
                    </FormControl>
                    <FormDescription>Analiz edilecek web sitesinin tam adresini girin.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField control={form.control} name="maxPages" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maksimum Sayfa Limiti</FormLabel>
                      <FormControl><Input type="number" placeholder="1000" {...field} /></FormControl>
                      <FormDescription>Taranacak maksimum sayfa sayısı (boş bırakırsanız limitsiz).</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="userAgent" render={({ field }) => (
                    <FormItem>
                      <FormLabel>User Agent</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormDescription>Taramada kullanılacak kimlik.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <FormField control={form.control} name="crawlSpeed" render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <div className="flex justify-between items-center mb-4">
                      <FormLabel>Tarama Hızı (Eşzamanlı İstek)</FormLabel>
                      <span className="font-medium text-sm px-2 py-1 bg-muted rounded-md">{value} istek/sn</span>
                    </div>
                    <FormControl><Slider min={1} max={10} step={1} value={[value]} onValueChange={(vals) => onChange(vals[0])} /></FormControl>
                    <FormDescription>Sunucuyu yormamak için ideal değer 3-5 arasıdır.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="respectRobotsTxt" render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Robots.txt Kurallarına Uy</FormLabel>
                      <FormDescription>Sitenin robots.txt dosyasındaki disallow kurallarına dikkat edilsin mi?</FormDescription>
                    </div>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />

                <FormField control={form.control} name="onlyContextualLinks" render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Sadece Metin İçindeki Linkleri Dahil Et</FormLabel>
                      <FormDescription>Açıkken: yalnızca makale/içerik bölümündeki bağlantılar analiz edilir. Kapalıyken: menü, header, footer ve sidebar linkleri de dahil edilir.</FormDescription>
                    </div>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />

                <FormField control={form.control} name="linkSuggestionScope" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base flex items-center gap-2"><Globe className="w-4 h-4" /> Link Önerisi Dil Kapsamı</FormLabel>
                    <FormDescription>Tüm site taranır; ancak AI link önerileri seçtiğin kapsamda üretilir. Tüm diller maliyeti artırır.</FormDescription>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                      {[
                        { v: "primary", title: "Tek dil (önerilen)", desc: "Sadece ana dil için link önerisi — düşük maliyet" },
                        { v: "all", title: "Tüm diller", desc: "Her dil için link önerisi — kapsamlı ama maliyetli" },
                      ].map((opt) => {
                        const active = field.value === opt.v;
                        return (
                          <button key={opt.v} type="button" onClick={() => field.onChange(opt.v)}
                            className="text-left rounded-lg border p-4 transition-colors"
                            style={{ borderColor: active ? "#e83e3c" : "var(--border)", background: active ? "rgba(232,62,60,0.08)" : "transparent" }}>
                            <div className="font-medium text-sm" style={{ color: active ? "#e83e3c" : undefined }}>{opt.title}</div>
                            <div className="text-xs text-muted-foreground mt-1">{opt.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                    <FormMessage />
                  </FormItem>
                )} />
              </CardContent>
              <CardFooter className="flex justify-end gap-4 border-t pt-6 bg-muted/20">
                <Button type="button" variant="outline" onClick={() => setLocation("/")}>İptal</Button>
                <Button type="submit" disabled={createDomain.isPending} className="gap-1.5">
                  {createDomain.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Taramayı Başlat <ArrowRight className="w-4 h-4" />
                </Button>
              </CardFooter>
            </form>
          </Form>
        </Card>
      )}

      {/* ═══ ADIM 2: GOOGLE ═══ */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-brand-500" /> Google Bağlantıları</CardTitle>
            <CardDescription>
              Tarama arka planda sürüyor — beklemene gerek yok. Search Console + Analytics bağlanınca
              trafik verilerin (tıklama, gösterim, kullanıcı, AI trafiği) tüm analizlere katılır.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {gAccount?.connected ? (
              /* Hesapta zaten bağlantı var: OAuth'u TEKRAR SORMA — tek tık yeniden kullan */
              <div className={stepBox} style={{ borderColor: "#12b76a55", background: "rgba(18,183,106,0.05)" }}>
                <div className="flex items-center gap-2 font-semibold text-sm"><CheckCircle2 className="w-4 h-4 text-success-500" /> Google hesabın zaten bağlı</div>
                <p className="text-[12px] text-muted-foreground mt-1">
                  {gAccount.email ?? "Hesap"} · {gAccount.fromDomain} sitesinde bağlı. Aynı izinler bu siteye de tanımlanır —
                  yeniden şifre/onay GEREKMEZ; bu sitenin Search Console ve Analytics eşleşmesi otomatik denenir.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Button className="gap-2 bg-brand-500 hover:bg-brand-600" disabled={!createdId || reusing} onClick={() => void reuseGoogle()}>
                    {reusing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Bu Siteye de Kullan
                  </Button>
                  <Button variant="outline" className="gap-2" disabled={!createdId}
                    onClick={() => { if (createdId) void startGoogleConnect(createdId, wizardUrl(3)); }}>
                    Farklı hesapla bağlan
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className={stepBox}>
                    <div className="font-semibold text-sm mb-1">Search Console</div>
                    <p className="text-[12px] text-muted-foreground">Hangi kelimeden kaç tıklama/gösterim aldığın — sıralama ve trafik düşüşü sinyallerinin kaynağı.</p>
                  </div>
                  <div className={stepBox}>
                    <div className="font-semibold text-sm mb-1">Google Analytics</div>
                    <p className="text-[12px] text-muted-foreground">Kullanıcı ve dönüşüm verisi — AI kaynaklı trafiği (ChatGPT, Perplexity...) ayrıştırmak dahil.</p>
                  </div>
                </div>
                <Button className="w-full sm:w-auto gap-2 bg-brand-500 hover:bg-brand-600" disabled={!createdId}
                  onClick={() => { if (createdId) void startGoogleConnect(createdId, wizardUrl(3)); }}>
                  <Globe className="w-4 h-4" /> Google ile Bağla
                </Button>
              </>
            )}
            <div className="flex items-start gap-2 text-[12px] rounded-lg px-3 py-2.5" style={{ background: "#fffbeb", color: "#b45309", border: "1px solid rgba(245,158,11,0.35)" }}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Bağlamazsan <strong>Trafik Analizi, trafik düşüşü uyarıları ve dönüşüm sinyalleri eksik kalır</strong>. Sonradan Trafik Analizi sayfasından da bağlayabilirsin.</span>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3 border-t pt-6 bg-muted/20">
            <Button variant="ghost" onClick={() => { setGoogleState("atlandi"); setStep(3); }}>Bu adımı atla</Button>
          </CardFooter>
        </Card>
      )}

      {/* ═══ ADIM 3: SIRALAMA TAKİBİ ═══ */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-brand-500" /> Sıralama Takibi</CardTitle>
            <CardDescription>
              Senin için en önemli kelimeleri yaz (virgül ya da satır ile ayır). Hacim ve zorlukları otomatik
              çekilir; <strong>her gece</strong> Google sıralaması + rakipler otomatik ölçülür. Bu adım ücretsizdir.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea value={kwText} onChange={(e) => setKwText(e.target.value)} rows={4}
              placeholder={"böcek ilaçlama istanbul\npire ilaçlama fiyatları\ntahtakurusu belirtileri"}
              className="w-full border rounded-lg px-3 py-2 text-[13px] bg-card" />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[12px] text-muted-foreground">Pazar:</span>
              <select value={kwMarket} onChange={(e) => setKwMarket(e.target.value)} className="border rounded-lg px-2 py-1.5 text-[13px] bg-card">
                {MARKETS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between gap-3 border-t pt-6 bg-muted/20">
            <Button variant="ghost" onClick={() => setStep(4)}>Bu adımı atla</Button>
            <Button className="gap-1.5 bg-brand-500 hover:bg-brand-600" disabled={!kwText.trim() || kwBusy} onClick={() => void addKeywords()}>
              {kwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />} Takibe Al <ArrowRight className="w-4 h-4" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* ═══ ADIM 4: AI TAKİBİ ═══ */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-brand-500" /> AI Takibi (GEO)</CardTitle>
            <CardDescription>
              İnsanlar artık yapay zekâya soruyor. Kelimelerinden yola çıkıp "yapay zekâya sorulacak soruları"
              otomatik öneriyorum — her gece 4 AI motoruna sorulur, markanın anılıp anılmadığı ölçülür. Ücretsizdir.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {aiSuggestions.length === 0 ? (
              <Button variant="outline" className="gap-2 border-brand-300 text-brand-600 dark:text-brand-400" disabled={aiBusy} onClick={() => void suggestAi()}>
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Soruları Otomatik Öner
              </Button>
            ) : (
              <div className="space-y-1.5">
                <div className="text-[12px] text-muted-foreground">Önerilen {aiSuggestions.length} soru (beğenmediğini çıkar):</div>
                {aiSuggestions.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-[13px]">
                    <span className="flex-1">{s}</span>
                    <button onClick={() => setAiSuggestions(aiSuggestions.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground text-xs">✕</button>
                  </div>
                ))}
              </div>
            )}
            {/* MANUEL SORU — öneri beklemeden (ya da önerilere ek) kendi sorunu yaz */}
            <div className="flex items-center gap-2">
              <input value={aiManuel} onChange={(e) => setAiManuel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") ekleAiManuel(); }}
                placeholder="Kendi sorunu yaz — örn. İstanbul'da en iyi böcek ilaçlama firması hangisi?"
                className="flex-1 border rounded-lg px-3 py-2 text-[13px] bg-card outline-none" />
              <Button size="sm" variant="outline" disabled={!aiManuel.trim()} onClick={ekleAiManuel} className="shrink-0 gap-1">
                <Plus className="w-3.5 h-3.5" /> Ekle
              </Button>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between gap-3 border-t pt-6 bg-muted/20">
            <Button variant="ghost" onClick={() => setStep(5)}>Bu adımı atla</Button>
            {aiSuggestions.length > 0 && (
              <Button className="gap-1.5 bg-brand-500 hover:bg-brand-600" disabled={aiBusy} onClick={() => void addAiPrompts()}>
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {aiSuggestions.length} Soruyu Takibe Al <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </CardFooter>
        </Card>
      )}

      {/* ═══ ADIM 5: BİTTİ ═══ */}
      {step === 5 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><PartyPopper className="w-5 h-5 text-brand-500" /> Kurulum Tamam!</CardTitle>
            <CardDescription>Genel tarama arka planda sürüyor; bittiğinde cluster yapını onaylaman istenecek.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { ok: true, text: "Genel site taraması başlatıldı (sayfalar, içerikler, iç linkler, cluster'lar)" },
              { ok: googleState === "bagli", text: googleState === "bagli" ? "Google bağlandı — trafik verileri analizlere katılacak" : "Google atlandı — Trafik Analizi'nden istediğin an bağlayabilirsin" },
              { ok: kwCount > 0, text: kwCount > 0 ? `${kwCount} kelime sıralama takibinde — her gece otomatik ölçülür` : "Sıralama takibi atlandı — Sıralama Takibi sayfasından ekleyebilirsin" },
              { ok: promptCount > 0, text: promptCount > 0 ? `${promptCount} AI sorusu takipte — her gece 4 motora sorulur` : "AI takibi atlandı — Sıralama Takibi > AI sekmesinden ekleyebilirsin" },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-2 text-[13px]">
                {s.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-success-500 shrink-0" /> : <span className="w-4 h-4 mt-0.5 shrink-0 text-center text-muted-foreground">—</span>}
                <span className={s.ok ? "" : "text-muted-foreground"}>{s.text}</span>
              </div>
            ))}
            <div className="text-[12px] text-muted-foreground rounded-lg border px-3 py-2.5 mt-3">
              💡 Tarama bitince <strong>Planlama</strong>'da "Strateji Seansı"nı başlat — beyin tüm sinyalleri okuyup ilk aylık planını seninle kurar.
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3 border-t pt-6 bg-muted/20">
            <Button variant="outline" onClick={() => setLocation("/planning")}>Planlama'yı Aç</Button>
            <Button className="gap-1.5 bg-brand-500 hover:bg-brand-600" onClick={() => setLocation("/")}>
              Dashboard'a Git <ArrowRight className="w-4 h-4" />
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
}
